// The pieces the Billing page and its bill panel share: a bill's colour and
// icon, the change against the month before, and how a bill reads by kind.
// Web twin of the mobile components/BillingParts.tsx.

import type { ReactElement } from 'react';
import type { BillingCategory, BillingSettlement } from '../lib/api';
import type { CategoryRow } from '../lib/billing';
import { Bs, mix, rgbString } from '../lib/colors';
import { useTheme } from '../lib/theme';
import MdiIcon from './MdiIcon';

export type BillKind = BillingCategory['kind'] | 'common';

// A bill's look by kind — the brand's water blue, a warm orange for heat,
// green for electricity — and, for a bill that only shares out what no meter
// of the home measured, the common-areas purple.
export function billKindStyle(kind: BillKind): { color: string; icon: string } {
  switch (kind) {
    case 'water':
      return { color: Bs.water, icon: 'mdi-water-outline' };
    case 'heat':
      return { color: Bs.orange, icon: 'mdi-fire' };
    case 'energy':
      return { color: Bs.success, icon: 'mdi-flash-outline' };
    case 'gas':
      return { color: Bs.danger, icon: 'mdi-gas-burner' };
    case 'fee':
      return { color: Bs.secondary, icon: 'mdi-receipt-text-outline' };
    case 'common':
    case 'other':
    default:
      return { color: Bs.purple, icon: 'mdi-office-building-outline' };
  }
}

// A bill that only shares out what no meter of the home measured reads as the
// common areas, whatever its kind.
export function rowKind(r: CategoryRow, s: BillingSettlement): BillKind {
  if (r.kind === 'fee') return 'fee';
  const cat = s.categories.find((c) => c.code === r.code);
  if (cat && cat.lines.length > 0 && cat.lines.every((l) => l.kind === 'common')) return 'common';
  return r.kind;
}

// The kind colour as text or a mark on the current theme's cards.
export function useKindTint(kind: BillKind): { color: string; ink: string; soft: string } {
  const { mode } = useTheme();
  const { color } = billKindStyle(kind);
  const dark = mode === 'dark';
  return {
    color,
    ink: dark ? mix(color, Bs.white, 0.35) : color,
    soft: dark ? `rgba(${rgbString(color)}, 0.22)` : mix(color, Bs.white, 0.85),
  };
}

// The bill's icon on a soft disc of its own colour, readable in both themes.
export function KindBubble({ kind }: { kind: BillKind }): ReactElement {
  const { ink, soft } = useKindTint(kind);
  return (
    <span className="icon-bubble" style={{ background: soft, color: ink }}>
      <MdiIcon name={billKindStyle(kind).icon} size={20} />
    </span>
  );
}

// A change against the month before: "▲ +6.2%", amber when up, green when
// down, plain inside ±0.5%. Spending less is the good news here.
export function ChangeText({ ratio }: { ratio: number | null }): ReactElement | null {
  if (ratio == null || !Number.isFinite(ratio)) return null;
  const pct = Math.abs(ratio * 100);
  if (pct < 0.5) return <span className="bill-change">±0%</span>;
  return (
    <span className={`bill-change ${ratio > 0 ? 'up' : 'down'}`}>
      {`${ratio > 0 ? '▲ +' : '▼ −'}${pct >= 10 ? pct.toFixed(0) : pct.toFixed(1)}%`}
    </span>
  );
}
